import type { CardId } from "./card"

/**
 * One job the room takes on: a problem statement, the cards chosen for it, where
 * those cards were filed, and what the deep dive said about the keepers.
 *
 * A job is what a session repeats. The room votes on pain points once, then
 * works them one job at a time: write the job → pick cards → file them → dive.
 * When a job is finished the facilitator can open another for a different pain,
 * and those rounds run again.
 *
 * Everything produced after the Pains round therefore belongs to a job, not to
 * the session — otherwise a second job would overwrite the first one's cards and
 * placements.
 *
 * The statement lives on this document rather than in a subcollection because
 * one job has exactly one statement, written by the facilitator from the vote.
 */
export interface Job {
  id: string
  /** The pain points this job answers, usually the ones that won the vote. */
  painIds: string[]
  /*
   * "As a <who>, I want <what>, so that <why>." With `userStoryEnabled` off the
   * room writes one plain sentence and only `what` is used — see `jobHolds`.
   */
  who: string
  what: string
  why: string
  /**
   * The facilitator's finalised shortlist, in the order they arranged it.
   *
   * Distinct from what the room picked, which lives in the `picks`
   * subcollection: the facilitator can drop a card several people chose and add
   * one nobody did, so a tally of picks cannot stand in for this. Empty until
   * they finalise.
   */
  cardIds: CardId[]
  /**
   * Whether the shortlist has been pre-filled from the room's nominations.
   *
   * The shortlist starts as the most-nominated cards and the facilitator drops
   * from there, which is much faster in a live room than keeping a dozen cards
   * one at a time. That seeding must happen exactly once: without this flag an
   * empty `cardIds` is ambiguous — it means both "not seeded yet" and "the
   * facilitator dropped everything" — and stepping back out of the shortlist and
   * in again would silently undo their work.
   */
  shortlistSeeded: boolean
  /**
   * Where each shortlisted card was filed, keyed by card id.
   *
   * A map on the job rather than a subcollection: a shortlist is a dozen cards
   * at most, every client already listens to this document, and the room watches
   * the facilitator file them live — one snapshot per move, with no second
   * listener to keep pointed at the right job.
   *
   * Patch it with dot-notation paths (`placements.<cardId>.bucket`); writing
   * `{ placements: partial }` would drop every card not in the patch.
   */
  placements: Record<CardId, CardPlacement>
  /**
   * The deep dive's four answers per card, keyed the same way. Only Must haves
   * reach it, but nothing here enforces that — a card demoted after the dive
   * keeps its answers, which is what the facilitator wants when they promote it
   * back.
   */
  deepDive: Record<CardId, DeepDiveDetail>
  /**
   * Which card the deep dive is on, for everyone.
   *
   * Shared rather than local because the round is run out loud: the facilitator
   * says "next one" and every screen has to follow, or half the room types
   * answers into a card nobody is discussing. Null until the facilitator moves
   * off the first card, and ignored if it names a card no longer in the pool —
   * dropping a card must not strand the round on it.
   */
  deepDiveCardId: CardId | null
  createdAt: number | null
  /** Set when the facilitator closes the job. Null while it is being worked. */
  completedAt: number | null
}

/**
 * MoSCoW. `wont` is spelled without the apostrophe because it is a stored key
 * and a document field, not copy — the label lives in `content/priorities`.
 */
export const MOSCOW_BUCKETS = ["must", "should", "could", "wont"] as const
export type MoscowBucket = (typeof MOSCOW_BUCKETS)[number]

export interface CardPlacement {
  bucket: MoscowBucket
  /** The facilitator's note for this card, in the room's words. */
  note: string
}

/** The four boxes the deep dive fills in for a Must have. */
export interface DeepDiveDetail {
  /** Whose job gets easier. */
  whoseJob: string
  /** The pain today. */
  painToday: string
  /** What number moves. */
  metric: string
  /** Where it lives in the product. */
  whereItLives: string
}

export const EMPTY_DEEP_DIVE: DeepDiveDetail = {
  whoseJob: "",
  painToday: "",
  metric: "",
  whereItLives: "",
}

/**
 * One participant's card selection for one job.
 *
 * A document per person, id = participant id, rather than an array of picks on
 * the job: everyone picks at the same time, and a single shared array would make
 * every pick a read-modify-write against everyone else's. Per-person documents
 * also give the facilitator's tally its denominator for free — how many people
 * have picked at all.
 */
export interface CardPick {
  /** The participant id (or the facilitator's uid). */
  id: string
  cardIds: CardId[]
  updatedAt: number | null
}

export type JobInput = Partial<Pick<Job, "who" | "what" | "why" | "painIds">>
